// Line Entry — the picker ("Select lines…" canvas board 2, "Add items…"
// board 7).
//
// Shows the candidates the engine built (`fetch_lines`) grouped by the
// document they come from, lets the user tick lines and edit the columns the
// source allows (`picker.edit`), and sends every edit back to the engine —
// debounced per row, ONE `recalc_lines` call for all rows edited in the
// window — so the line form's own item_change, limits and (in "object" mode)
// validations answer for each row. Nothing is computed here. "Add" runs
// `apply_lines`, the authoritative pass.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, Search, X } from 'lucide-react'
import { Z_RECORD_DIALOG } from '../../layers'
import type {
  FetchLinesResponse, LineEntryApi, LinePickerColumn, LineRowResult,
} from './contract'
import { createRecalcQueue, type RecalcQueue } from './recalcQueue'
import type { LineCmd } from './useLineEntry'
import DocumentChooser from './DocumentChooser'
import {
  T, STATUS_TONE, IDLE_TONE, ERROR_TONE, btn, btnPri, btnSm, chip, kbdOnPri, overlay, dialog, dialogFoot,
} from './styles'

interface Props {
  api: LineEntryApi
  cmd: LineCmd
  first: FetchLinesResponse
  documents?: unknown[]
  /** The source offers a document chooser ("+ another PO"). */
  canChooseMore?: boolean
  /** The criteria these lines were read with (a General Process source). */
  criteriaRecap?: string
  /** Back to the criteria step. */
  onChangeCriteria?: () => void
  onCancel: () => void
  /** apply_lines produced these lines — the host appends them. */
  onDone: (rows: LineRowResult[]) => void
  onError: (what: string, err: unknown) => void
}

const RECALC_DELAY_MS = 350

function isNumType(t: string | undefined): boolean {
  return /INT|NUM|DEC|FLOAT|DOUBLE|REAL|MONEY/i.test(String(t || ''))
}

function fmt(v: unknown): string {
  if (v == null || v === '') return ''
  if (typeof v === 'number') {
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 4 })
  }
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function inputKind(colType: string | undefined): 'number' | 'date' | 'datetime-local' | 'text' {
  const t = String(colType || '').toUpperCase()
  if (isNumType(t)) return 'number'
  if (t === 'DATE') return 'date'
  if (/TIMESTAMP|DATETIME/.test(t)) return 'datetime-local'
  return 'text'
}

function hasErrors(r: LineRowResult): boolean {
  return !!r._row_error || Object.keys(r._errors || {}).length > 0
}

function statusText(r: LineRowResult): string {
  const s = r._status
  if (!s) return ''
  const d = typeof s.delta === 'number' ? Math.abs(s.delta) : null
  return d ? `${s.label} ${fmt(d)}` : s.label
}

export default function LinePicker({
  api, cmd, first, documents: initialDocs, canChooseMore, criteriaRecap, onChangeCriteria,
  onCancel, onDone, onError,
}: Props) {
  const multi = first.picker === 'multi'
  const [docs, setDocs] = useState<unknown[]>(initialDocs || [])
  const [rows, setRows] = useState<LineRowResult[]>(first.rows)
  const [total, setTotal] = useState(first.total)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(first.rows.filter(r => r._selected && !r._linked).map(r => r._src_key)),
  )
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [recalcBusy, setRecalcBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(first.message || null)
  const [choosing, setChoosing] = useState(false)
  const edits = useRef(new Map<string, Record<string, unknown>>())
  const cols: LinePickerColumn[] = first.columns
  const opts = first.options
  const statusCol = cols.find(c => c.status)
  const noun = first.source_label || 'lines'

  const rowsRef = useRef(rows)
  rowsRef.current = rows

  const queueRef = useRef<RecalcQueue | null>(null)
  if (!queueRef.current) {
    queueRef.current = createRecalcQueue({
      delay: RECALC_DELAY_MS,
      send: (batch) => {
        setRecalcBusy(true)
        return api.recalc(cmd.name, batch, docs.length ? docs : undefined).finally(() => {
          if (!queueRef.current?.pending()) setRecalcBusy(false)
        })
      },
      onResult: (r) => setRows(prev => prev.map(x => (x._src_key === r._src_key
        ? { ...x, ...r, _linked: x._linked, _display: x._display, _show: x._show, _document: x._document }
        : x))),
      onError: (err) => onError('Recalculating the line failed', err),
    })
  }
  useEffect(() => () => queueRef.current?.dispose(), [])

  const refetch = useCallback(async (nextDocs: unknown[], q: string, keepEdits = true) => {
    setLoading(true)
    try {
      const res = await api.fetch(cmd.name, { documents: nextDocs.length ? nextDocs : undefined, search: q })
      setRows(res.rows.map(r => {
        const e = keepEdits ? edits.current.get(r._src_key) : undefined
        return e ? { ...r, values: { ...r.values, ...e } } : r
      }))
      setTotal(res.total)
      // Ticks survive a filter (they are kept by source key, visible or
      // not); rows seen for the first time take the engine's default; a
      // reset starts from the defaults again.
      const known = new Set(rowsRef.current.map(r => r._src_key))
      setSelected(prev => {
        const next = keepEdits ? new Set(prev) : new Set<string>()
        for (const r of res.rows) {
          if (r._linked) { next.delete(r._src_key); continue }
          if (r._selected && (!keepEdits || !known.has(r._src_key))) next.add(r._src_key)
        }
        return next
      })
    } catch (err) {
      onError('Loading lines failed', err)
    } finally {
      setLoading(false)
    }
  }, [api, cmd.name, onError])

  // Server-side search (debounced): ticks and edits are kept by source key,
  // so narrowing never loses what was chosen.
  const firstSearch = useRef(true)
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return }
    const h = setTimeout(() => { void refetch(docs, search) }, 300)
    return () => clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const loadMore = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.fetch(cmd.name, { documents: docs.length ? docs : undefined, search, offset: rows.length })
      setRows(prev => [...prev, ...res.rows])
      setTotal(res.total)
      setSelected(prev => {
        const next = new Set(prev)
        for (const r of res.rows) if (r._selected && !r._linked) next.add(r._src_key)
        return next
      })
    } catch (err) {
      onError('Loading more lines failed', err)
    } finally {
      setLoading(false)
    }
  }, [api, cmd.name, docs, search, rows.length, onError])

  const editCell = useCallback((key: string, col: string, raw: string, kind: string) => {
    const v: unknown = kind === 'number' ? (raw === '' ? null : Number(raw)) : raw
    const mine = { ...(edits.current.get(key) || {}), [col]: v }
    edits.current.set(key, mine)
    const cur = rowsRef.current.find(r => r._src_key === key)
    const nextValues = { ...(cur?.values || {}), ...mine }
    setRows(prev => prev.map(r => (r._src_key === key ? { ...r, values: { ...r.values, [col]: v } } : r)))
    setSelected(prev => (prev.has(key) ? prev : new Set(prev).add(key)))
    queueRef.current?.edit(key, nextValues, col)
  }, [])

  const selectable = rows.filter(r => !r._linked)
  const allOn = selectable.length > 0 && selectable.every(r => selected.has(r._src_key))
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(selectable.map(r => r._src_key)))
  const toggle = (k: string) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(k)) next.delete(k)
    else next.add(k)
    return next
  })

  const chosen = rows.filter(r => selected.has(r._src_key))
  const withErrors = chosen.filter(hasErrors)
  const blockAdd = opts.on_invalid === 'block_add' && withErrors.length > 0
  const totalsCols = (opts.totals || []).map(k => cols.find(c => c.key === k && c.kind === 'line'))
    .filter((c): c is LinePickerColumn => !!c)
  const sums = totalsCols.map(c => {
    let s = 0
    for (const r of chosen) {
      const n = Number(r.values[c.key])
      if (Number.isFinite(n)) s += n
    }
    return { label: c.label, value: s }
  })
  const exceptions = useMemo(() => {
    const byLabel = new Map<string, number>()
    for (const r of chosen) {
      if (r._status && r._status.index !== 1 && r._status.label) {
        byLabel.set(r._status.label, (byLabel.get(r._status.label) || 0) + 1)
      }
    }
    return Array.from(byLabel.entries()).map(([l, n]) => `${n} ${l.toLowerCase()}`)
  }, [chosen])

  // Rows grouped by the document they come from, in first-seen order.
  const groups = useMemo(() => {
    const order: string[] = []
    const by = new Map<string, LineRowResult[]>()
    for (const r of rows) {
      const g = r._document == null ? '' : String(r._document)
      if (!by.has(g)) { by.set(g, []); order.push(g) }
      by.get(g)!.push(r)
    }
    return order.map(g => ({ doc: g, rows: by.get(g)! }))
  }, [rows])
  const showGroups = !multi && groups.length > 0 && groups.some(g => g.doc)

  const add = useCallback(async () => {
    if (adding || selected.size === 0 || blockAdd) return
    setAdding(true)
    setNotice(null)
    try {
      await queueRef.current?.flush()
      const linkedKeys = new Set(rowsRef.current.filter(r => r._linked).map(r => r._src_key))
      const picks = Array.from(selected).filter(k => !linkedKeys.has(k))
        .map(k => ({ _src_key: k, values: edits.current.get(k) || {} }))
      const res = await api.apply(cmd.name, picks, docs.length ? docs : undefined)
      const produced = res.rows || []
      const bad = produced.filter(r => hasErrors(r) && !r._linked)
      if (bad.length && (res.options?.on_invalid || opts.on_invalid) === 'block_add') {
        const byKey = new Map(bad.map(r => [r._src_key, r]))
        setRows(prev => prev.map(r => (byKey.has(r._src_key)
          ? { ...r, ...byKey.get(r._src_key)!, _display: r._display, _show: r._show, _document: r._document } : r)))
        setNotice(`${bad.length} line${bad.length === 1 ? '' : 's'} need attention before they can be added.`)
        return
      }
      const good = produced.filter(r => !hasErrors(r) || r._linked)
      if (bad.length) {
        onDone(good)
        const byKey = new Map(bad.map(r => [r._src_key, r]))
        setRows(prev => prev.filter(r => byKey.has(r._src_key))
          .map(r => ({ ...r, ...byKey.get(r._src_key)!, _display: r._display, _show: r._show, _document: r._document })))
        setSelected(new Set(bad.map(r => r._src_key)))
        setNotice(`${good.length} added · ${bad.length} with errors left here.`)
        return
      }
      onDone(produced)
    } catch (err) {
      onError(`${cmd.label} failed`, err)
    } finally {
      setAdding(false)
    }
  }, [adding, selected, blockAdd, api, cmd, docs, onDone, onError, opts.on_invalid])

  const reset = () => {
    edits.current.clear()
    void refetch(docs, search, false)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onCancel(); return }
    if (e.key !== 'Enter') return
    const inQty = (e.target as HTMLElement)?.dataset?.leQty === '1'
    const inSearch = (e.target as HTMLElement)?.dataset?.leSearch === '1'
    if (e.ctrlKey || e.metaKey || (!multi && !inQty && !inSearch)) {
      e.preventDefault()
      void add()
      return
    }
    if (inQty) {
      // Enter in a quantity moves down a row, as in a spreadsheet.
      e.preventDefault()
      const all = Array.from(document.querySelectorAll<HTMLInputElement>('input[data-le-qty="1"]'))
      const i = all.indexOf(e.target as HTMLInputElement)
      all[i + 1]?.focus()
      all[i + 1]?.select()
    }
  }

  const count = selected.size - (opts.on_invalid === 'skip_invalid' ? withErrors.length : 0)
  const addLabel = blockAdd
    ? `Fix ${withErrors.length} line${withErrors.length === 1 ? '' : 's'} to add`
    : `Add ${count} line${count === 1 ? '' : 's'}`
  const disabled = adding || selected.size === 0 || blockAdd
  const firstEdit = cols.find(c => c.kind === 'line' && c.editable)

  const title = multi ? (first.command_label || cmd.label).replace(/…$/, '') : `Lines from ${noun}`
  const subtitle = multi ? 'Tick as many as you need — one line each' : (cmd.label || '').replace(/…$/, '')

  const renderRow = (r: LineRowResult) => {
    const on = selected.has(r._src_key)
    const linked = !!r._linked
    const warnText = (r._warnings || []).map(w => w.message).join('\n')
    const tone = !on ? IDLE_TONE : (r._status ? (STATUS_TONE[r._status.index] || IDLE_TONE) : IDLE_TONE)
    return (
      <tr key={r._src_key} style={{
        borderBottom: '1px solid #edf0f4', height: 42,
        background: r._row_error ? '#fff6f6' : on ? T.bg : '#fbfcfd', color: linked ? '#8a94a3' : undefined,
      }}>
        <td style={td}>
          <input type="checkbox" checked={on || linked} disabled={linked} onChange={() => toggle(r._src_key)}
            aria-label={linked ? 'Already on this record' : `Select line ${r._src_key}`} />
        </td>
        {cols.map(c => {
          const val = c.kind === 'line' ? r.values[c.key] : c.kind === 'source' ? r._display?.[c.key] : r._show?.[c.key]
          const err = c.kind === 'line' ? r._errors?.[c.key] : undefined
          const editable = c.kind === 'line' && c.editable && !linked && r._protect?.[c.key] !== 1
            && (!multi || on)
          const kind = inputKind(c.col_type)
          const num = kind === 'number' || typeof val === 'number'
          return (
            <td key={`${c.kind}:${c.key}`} title={err || undefined}
              style={{ ...td, textAlign: num ? 'right' : 'left', fontFamily: num || c.key === 'code' ? T.mono : undefined,
                fontWeight: c.status && !editable ? 600 : undefined }}>
              {editable ? (
                <input
                  type={kind} data-le-qty={kind === 'number' ? '1' : undefined}
                  value={val == null ? '' : String(val)}
                  onChange={e => editCell(r._src_key, c.key, e.target.value, kind)}
                  aria-label={`${c.label} for line ${r._src_key}`} aria-invalid={!!err}
                  style={{
                    width: kind === 'number' ? 96 : 140, height: 30, boxSizing: 'border-box',
                    textAlign: kind === 'number' ? 'right' : 'left', padding: '0 8px', borderRadius: 5,
                    border: `1px solid ${err ? '#d93636' : '#c9d2dc'}`, boxShadow: err ? 'inset 0 0 0 1px #d93636' : undefined,
                    fontFamily: kind === 'number' ? T.mono : 'inherit', fontSize: 14, fontWeight: 500,
                    color: T.text, background: T.bg,
                  }}
                />
              ) : (
                <span style={{ color: err ? ERROR_TONE.fg : undefined }}>{fmt(val)}</span>
              )}
              {err && <div style={{ color: ERROR_TONE.fg, fontSize: 13, whiteSpace: 'normal', textAlign: 'left' }}>{err}</div>}
            </td>
          )
        })}
        {statusCol && (
          <td style={td}>
            {linked ? (
              <span style={{ ...chip, background: IDLE_TONE.bg, color: IDLE_TONE.fg }}>On this record</span>
            ) : r._row_error ? (
              <span title={r._row_error} style={{ ...chip, background: ERROR_TONE.bg, color: ERROR_TONE.fg }}>Needs attention</span>
            ) : (
              <span style={{ ...chip, background: tone.bg, color: tone.fg }}>
                {on ? (statusText(r) || '—') : 'Not added'}
              </span>
            )}
            {warnText && (
              <span title={warnText} style={{ marginLeft: 6, color: '#9a4a00', verticalAlign: 'middle' }}>
                <AlertTriangle size={12} aria-label={warnText} />
              </span>
            )}
          </td>
        )}
      </tr>
    )
  }

  return (
    <div role="presentation" onClick={onCancel} style={{ ...overlay, zIndex: Z_RECORD_DIALOG }}>
      <div role="dialog" aria-modal="true" aria-labelledby="le-pick-title" onClick={e => e.stopPropagation()}
        onKeyDown={onKey} style={{ ...dialog, maxWidth: multi ? 1000 : 1180 }}>
        <div style={{ padding: '18px 22px 14px', display: 'flex', flexDirection: 'column', gap: 12,
          borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 id="le-pick-title" style={{ margin: 0, fontSize: 19, fontWeight: 600 }}>{title}</h2>
            <span style={{ fontSize: 14, color: T.muted }}>{subtitle}</span>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={onCancel} aria-label="Close" style={{ ...btn, padding: '0 10px' }}><X size={16} /></button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {!multi && docs.map(d => {
              const n = rows.filter(r => String(r._document ?? '') === String(d)).length
              return (
                <span key={String(d)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 32,
                  padding: '0 12px', borderRadius: 16, background: T.accent, color: '#fff', fontSize: 14 }}>
                  <span style={{ fontFamily: T.mono }}>{String(d)}</span>{n ? ` · ${n.toLocaleString()} ${n === 1 ? 'line' : 'lines'}` : ''}
                  {docs.length > 1 && (
                    <button type="button" aria-label={`Remove ${String(d)}`}
                      onClick={() => { const next = docs.filter(x => String(x) !== String(d)); setDocs(next); void refetch(next, search) }}
                      style={{ background: 'none', border: 0, color: '#fff', cursor: 'pointer', padding: 0, display: 'inline-flex' }}>
                      <X size={13} />
                    </button>
                  )}
                </span>
              )
            })}
            {onChangeCriteria && (
              <button type="button" onClick={onChangeCriteria} title="Change the criteria"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 16,
                  background: T.band, color: T.text, border: `1px solid ${T.border}`, fontSize: 14,
                  cursor: 'pointer', fontFamily: 'inherit', maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' }}>
                {criteriaRecap || 'Criteria'} <span style={{ color: T.accent, fontWeight: 500 }}>· Change</span>
              </button>
            )}
            {!multi && canChooseMore && (
              <button type="button" onClick={() => setChoosing(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 16,
                  background: T.bg, color: T.accent, border: `1px dashed ${T.accent}`, fontSize: 14, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit' }}>
                + another document
              </button>
            )}
            <span style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, border: '1px solid #c9d2dc',
              borderRadius: 6, padding: '0 10px', minWidth: 220, color: T.muted, boxSizing: 'border-box' }}>
              <Search size={16} aria-hidden="true" />
              <input data-le-search="1" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={multi ? 'Search' : 'Filter by item or code'} aria-label="Filter lines"
                style={{ border: 0, outline: 0, flex: 1, minWidth: 0, fontSize: 14, background: 'transparent', color: T.text }} />
              {loading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            </label>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 760, fontSize: 14 }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f0f3f7', height: 36 }}>
                <th style={th}>
                  <input type="checkbox" checked={allOn} onChange={toggleAll}
                    aria-label="Select all lines" disabled={!selectable.length} />
                </th>
                {cols.map(c => (
                  <th key={`${c.kind}:${c.key}`} style={{ ...th, textAlign: isNumType(c.col_type) ? 'right' : 'left',
                    color: c.editable ? T.accent : undefined }}>{c.label}</th>
                ))}
                {statusCol && <th style={th}>{rows.some(r => r._status?.delta != null) ? 'Difference' : 'Status'}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={cols.length + 2} style={{ padding: 24, textAlign: 'center', color: T.muted }}>No lines match.</td></tr>
              )}
              {showGroups ? groups.map(g => (
                <GroupRows key={g.doc || '-'} doc={g.doc} count={g.rows.length}
                  linked={g.rows.filter(r => r._linked).length} span={cols.length + (statusCol ? 2 : 1)}>
                  {g.rows.map(renderRow)}
                </GroupRows>
              )) : rows.map(renderRow)}
            </tbody>
          </table>
          {rows.length < total && (
            <div style={{ padding: '10px', fontSize: 14, borderBottom: '1px solid #edf0f4' }}>
              <button type="button" onClick={() => { void loadMore() }} disabled={loading} style={btnSm}>
                + {(total - rows.length).toLocaleString()} more {noun.toLowerCase()} — show them
              </button>
            </div>
          )}
        </div>

        {multi && chosen.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '10px 22px', borderTop: `1px solid ${T.border}` }}>
            {chosen.map(r => (
              <button key={r._src_key} type="button" onClick={() => toggle(r._src_key)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px', borderRadius: 14,
                  border: `1px solid ${T.border}`, background: '#f6f9ff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: T.text }}>
                <span style={{ fontFamily: T.mono }}>
                  {firstEdit && r.values[firstEdit.key] != null ? `${fmt(r.values[firstEdit.key])} × ` : ''}{r._src_key}
                </span>
                <X size={12} aria-hidden="true" /><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Remove</span>
              </button>
            ))}
          </div>
        )}

        {rows.some(r => r._row_error && selected.has(r._src_key)) && (
          <div style={{ padding: '6px 22px', borderTop: `1px solid ${T.border}`, fontSize: 13, color: ERROR_TONE.fg }}>
            {rows.filter(r => r._row_error && selected.has(r._src_key)).slice(0, 3).map(r => (
              <div key={r._src_key}><span style={{ fontFamily: T.mono }}>{r._src_key}</span>: {r._row_error}</div>
            ))}
          </div>
        )}

        <div style={dialogFoot}>
          <span style={{ fontSize: 15 }}>
            <b>{selected.size.toLocaleString()}</b> {selected.size === 1 ? 'line' : 'lines'} selected
            {sums.map(s => (
              <span key={s.label}> · <b style={{ fontFamily: T.mono }}>{fmt(s.value)}</b> {s.label.toLowerCase()}</span>
            ))}
          </span>
          <span style={{ fontSize: 13, color: T.muted }}>
            {exceptions.length ? `(${exceptions.join(', ')})` : ''}
            {recalcBusy ? ' updating…' : ''}
          </span>
          {notice && <span style={{ fontSize: 13, color: '#9a4a00' }}>{notice}</span>}
          <span style={{ flex: 1 }} />
          {!multi && edits.current.size > 0 && (
            <button type="button" onClick={reset} style={btn}>Reset to the pre-filled values</button>
          )}
          {multi && <button type="button" onClick={onCancel} style={btn}>Cancel</button>}
          <button type="button" onClick={() => { void add() }} disabled={disabled}
            style={{ ...btnPri, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
            {adding && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {addLabel}
            <span style={kbdOnPri}>{multi ? 'Ctrl Enter' : 'Enter'}</span>
          </button>
        </div>
      </div>

      {choosing && (
        <div onClick={e => e.stopPropagation()}>
          <DocumentChooser
            api={api} cmd={{ ...cmd, picker: 'review' }} noun={noun}
            onCancel={() => setChoosing(false)}
            onError={onError}
            onChoose={(more) => {
              setChoosing(false)
              const next = [...docs, ...more.filter(m => !docs.some(d => String(d) === String(m)))]
              setDocs(next)
              void refetch(next, search)
            }}
          />
        </div>
      )}
    </div>
  )
}

function GroupRows({ doc, count, linked, span, children }: {
  doc: string; count: number; linked: number; span: number; children: React.ReactNode
}) {
  return (
    <>
      {doc && (
        <tr>
          <td colSpan={span} style={{ background: '#f8fafc', fontSize: 13, color: T.muted, padding: '7px 10px',
            borderBottom: '1px solid #edf0f4' }}>
            <b style={{ fontFamily: T.mono, color: T.text }}>{doc}</b> · {count.toLocaleString()} {count === 1 ? 'line' : 'lines'}
            {linked ? `, ${linked} already on this record` : ''}
          </td>
        </tr>
      )}
      {children}
    </>
  )
}

const th: React.CSSProperties = {
  padding: '0 10px', fontWeight: 600, fontSize: 13, color: T.muted, whiteSpace: 'nowrap', textAlign: 'left',
  borderBottom: `1px solid ${T.border}`,
}
const td: React.CSSProperties = { padding: '0 10px', whiteSpace: 'nowrap', verticalAlign: 'middle' }
